import React, { useState, useEffect, useRef } from 'react';
import { Button, Dropdown, Image, Stack } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { useNavigate } from 'react-router-dom';
import { S3_URL } from 'constants';
import parse from 'html-react-parser';
import { getCreatedDifferenceInBestUnit, unitNumber } from 'utils';
import FavoriteBtn from 'components/Common/ActionButton/FavoriteBtn';
import JobApplyBtn from 'components/Common/ActionButton/JobApplyBtn';
import useAuthStore from 'store/auth';
import useTranslate from 'hooks/useTranslate';
import useCommonStore, { setModalConfig } from 'store/common';
import { useDeleteJobMutation, useRemoveAssessmentMutation } from 'services';
import toast from 'react-hot-toast';
import './styles.scss';
import { USER_TYPES } from 'constants';

const JobListingBox: React.FC<any> = ({
	job,
	enableFavBtn = true,
	enableApplyBtn = true,
	enableAddress = true,
	refetch = () => null,
	dataLength = 0,
	currentPage = 1,
	setCurrentPage = () => null,
	enableKebabIcon = false,
}) => {
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);
	const [isFavourite, setIsFavourite] = useState<boolean>(false);
	const [isJobApply, setIsJobApply] = useState<boolean>(false);
	const [assessment, setAssessment] = useState<boolean>(job.isJobAssessment === 1);
	const [jobApplyStatus, setJobApplyStatus] = useState<boolean>(false);
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const { mutateAsync: DeleteJob } = useDeleteJobMutation();
	const { mutateAsync: removeAssessmentMutation } = useRemoveAssessmentMutation();

	const [hasMore, setHasMore] = useState(false);
	const descriptionRef = useRef(null);

	useEffect(() => {
		setIsJobApply(job?.isJobApply || false);
		setJobApplyStatus(job?.jobApplyStatus || 'Applied');
		setIsFavourite(job?.isFavourite || false);
	}, [job]);

	useEffect(() => {
		const element: any = descriptionRef?.current;
		if (element?.scrollHeight > element?.clientHeight) {
			setHasMore(true);
		}
	}, [job?.description]);

	const { token, user: authUser }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	const onCardClick = (id: number) => {
		navigate(`/job-detail/${id}`);
	};

	const onHandleDelete = (id: number | null = null) => {
		setModalConfig({
			visible: true,
			id: id,
			type: 'confirmModal',
			onClick: () => onHandleYes(id),
			data: {
				title: translate('Modal.wantToDelete', { title: translate('Modal.job') }),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const onHandleYes = (id: number | null) => {
		DeleteJob(id)
			.then((res: any) => {
				if (res?.success) {
					toast.success(res?.message);
					refetch();
				} else {
					toast.error(res?.message);
				}
			})
			.finally(() => {
				hideCommonModal();
			});
	};

	const handleClick = (id: number | null = null) => {
		if (!authUser?.userId) {
			setModalConfig({
				visible: true,
				id: id,
				type: 'loginModal',
			});
			return false;
		}
		return true;
	};

	return (
		<Stack className="posted-job-box" onClick={() => onCardClick(job?.job_id)}>
			<div className="posted-job-box-content d-flex gap-3 position-relative">
				<div className="d-flex flex-column flex-md-row align-items-md-center flex-1 gap-3">
					<div className="company-logo">
						<Image
							src={
								job?.companyLogo ? `${S3_URL}${job.companyLogo}` : IMAGE_PATHS.CompanyLogoDefault
							}
							alt="logo"
							className="w-100 h-100 object-fit-cover"
							loading="lazy"
						/>
					</div>
					<Stack className="job-detail justify-content-center flex-1">
						{job?.title && <h4 className="mb-0 fw-bold">{job.title}</h4>}
						{job?.companyName && <p className="company-name mb-0">{job.companyName}</p>}
						{enableAddress && (
							<p className="mb-0">
								<i className="ri-map-pin-line"></i>
								{[job?.cityName, job?.stateName, job?.countryName].filter(Boolean).join(', ') ?? ''}
							</p>
						)}
					</Stack>
				</div>

				<div className="apply-btn d-flex gap-2 gap-md-3">
					<div>
						{enableApplyBtn && token && (
							<JobApplyBtn
								isApplied={isJobApply}
								setIsApplied={setIsJobApply}
								jobId={Number(job?.jobId)}
								isJobAssessment={job?.isJobAssessment || false}
								handleClick={handleClick}
								modalConfig={modalConfig}
								setModalConfig={setModalConfig}
								jobApplyStatus={jobApplyStatus}
							/>
						)}
					</div>
					{enableFavBtn && (
						<FavoriteBtn
							isFavourite={isFavourite}
							setIsFavourite={setIsFavourite}
							jobId={Number(job?.jobId)}
							refetch={refetch}
							dataLength={dataLength}
							currentPage={currentPage}
							setCurrentPage={setCurrentPage}
							handleClick={handleClick}
						/>
					)}
				</div>

				{enableKebabIcon && authUser?.userType === USER_TYPES?.COMPANY && (
					<div className="edit-action">
						<Dropdown onClick={(evt) => evt.stopPropagation()}>
							<Dropdown.Toggle
								variant="success"
								id="dropdown-basic"
								className="p-0 border-0 rounded-circle"
							>
								<i className="ri-more-2-fill"></i>
							</Dropdown.Toggle>

							<Dropdown.Menu>
								{authUser?.userId === job?.companyId && (
									<>
										{job.totalJobApply === 0 && (
											<Dropdown.Item onClick={() => navigate(`/company/edit-job/${job?.jobId}`)}>
												<i className="ri-edit-line"></i> {translate('Common.edit')}
											</Dropdown.Item>
										)}
										<Dropdown.Item onClick={() => onHandleDelete(job?.jobId)}>
											<i className="ri-delete-bin-7-line"></i> {translate('Common.delete')}
										</Dropdown.Item>

										{assessment && job.totalJobApply === 0 && (
											<>
												<Dropdown.Item
													onClick={() => navigate(`/company/edit-questionnaire/${job?.jobId}`)}
												>
													<i className="ri-edit-line"></i>{' '}
													{translate('Questionnaire.editQuestionnaire')}
												</Dropdown.Item>
												<Dropdown.Item
													onClick={() => {
														removeAssessmentMutation(job?.jobAssessmentId).then((res: any) => {
															if (res.success) setAssessment(false);
														});
													}}
												>
													<i className="ri-edit-line"></i>{' '}
													{translate('Questionnaire.removeAssesment')}
												</Dropdown.Item>
											</>
										)}
									</>
								)}
							</Dropdown.Menu>
						</Dropdown>
					</div>
				)}
			</div>
			{job?.description?.length > 0 && (
				<div className="posted-job-box-description">
					<p
						ref={descriptionRef}
						className={`mb-0 description-truncated ${hasMore ? 'has-more' : ''}`}
					>
						{parse(job?.description)}
					</p>
				</div>
			)}
			<div className="posted-job-box-basic-info d-flex flex-wrap align-items-center">
				{job?.jobType && (
					<div className="job-info d-flex align-items-center gap-2">
						<i className="ri-time-line"></i>
						<h6 className="mb-0 fw-bold">{job.jobType}</h6>
					</div>
				)}
				<span className="info-separator"></span>
				<div className="job-info d-flex align-items-center gap-2">
					<i className="ri-wallet-3-line"></i>
					<h6 className="mb-0 fw-bold">
						{job?.currency}
						{[unitNumber(job?.minAmount, 1), unitNumber(job?.maxAmount, 1)]
							.filter(Boolean)
							.join('-') ?? ''}
						<span className="ps-1">{job?.salaryType && `(${job.salaryType})`}</span>
					</h6>
					<span className="info-separator"></span>
				</div>

				<div className="job-info d-flex align-items-center gap-2">
					<i className="ri-group-line"></i>
					<h6 className="mb-0 fw-bold">{unitNumber(job.totalJobApply, 0)}</h6>
				</div>
				<span className="info-separator"></span>

				{job?.level?.title && (
					<>
						<div className="job-info d-flex align-items-center gap-2">
							<i className="ri-award-line"></i>
							<h6 className="mb-0 fw-bold">{job?.level?.title}</h6>
						</div>
						<span className="info-separator"></span>
					</>
				)}

				<div className="job-info d-flex align-items-center gap-2">
					<i className="ri-list-check-2"></i>
					<h6 className="mb-0 fw-bold">
						{translate('JobDetailBox.assessment')}:{' '}
						{assessment ? translate('Common.yes') : translate('Common.no')}
					</h6>
				</div>
				<span className="info-separator"></span>

				{job?.createdAt && (
					<div className="job-info d-flex align-items-center gap-2">
						<i className="ri-calendar-line"></i>
						<h6 className="mb-0 fw-bold">{getCreatedDifferenceInBestUnit(job.createdAt)}</h6>
					</div>
				)}
			</div>
			{enableKebabIcon && authUser?.userId === job?.companyId && (
				<div className="posted-job-box-actions d-flex gap-2 gap-sm-3 justify-content-sm-end mt-auto">
					{job.totalJobApply > 0 && (
						<>
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/company/applicants/${job?.jobId}`);
								}}
								className="transparent-btn rounded-pill lh-1"
							>
								{translate('Company.applicants')}
							</Button>
							<Button
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/company/hiring-board/${job?.jobId}`);
								}}
								className="filled-btn rounded-pill lh-1"
							>
								{translate('Company.applicationBoard')}
							</Button>
						</>
					)}
				</div>
			)}
		</Stack>
	);
};

export default React.memo(JobListingBox);
